import { useCompanyTz } from '@/lib/companyTime';
import { groupRecents } from './recentGroups';
import { GroupHead, Note, OptionRow, SeeAllRow } from './SmartSearchRows';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function RecentSection({ opts, nav, q, onClear }) {
  const tz = useCompanyTz();
  const groups = groupRecents(opts.map((o) => ({ ...o, opened_at: o.row.opened_at })), tz);
  const clear = (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClear} data-testid="topbar-search-recent-clear"
      className="normal-case tracking-normal text-[11px] font-semibold text-[#4575A8] hover:underline">Clear recent activity</button>
  );
  return (
    <div data-testid="topbar-search-recent">
      <GroupHead right={clear}>Recent activity</GroupHead>
      {groups.map((g) => (
        <div key={g.label} data-testid={`topbar-search-recent-${slug(g.label)}`}>
          <p className="px-4 pt-1.5 pb-0.5 text-[10px] font-medium" style={{ color: 'var(--strata-text-secondary)' }}>{g.label}</p>
          <ul role="group" aria-label={g.label}>
            {g.items.map((o) => (
              <OptionRow key={o.key} opt={o} q={q} showType {...nav(o)} testid={`topbar-search-recent-row-${o.type}-${o.row.id}`} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ResultGroup({ s, g, opts, nav, q }) {
  return (
    <div data-testid={`topbar-search-group-${s.key}`} className="border-t first:border-t-0" style={{ borderColor: 'var(--strata-border)' }}>
      <GroupHead right={g.status === 'ready' && <span className="normal-case tracking-normal">{g.total}</span>}>{s.title}</GroupHead>
      {g.status === 'slow' && <Note testid={`topbar-search-slow-${s.key}`}>{s.title} took too long to answer. Keep typing or try again.</Note>}
      {g.status === 'error' && <Note testid={`topbar-search-error-${s.key}`}>{s.title} search isn't available right now. Try again.</Note>}
      {opts.length > 0 && (
        <ul role="group" aria-label={s.title}>
          {opts.map((o) => (o.kind === 'all' ? <SeeAllRow key={o.key} opt={o} {...nav(o)} />
            : <OptionRow key={o.key} opt={o} q={q} {...nav(o)} testid={`topbar-search-row-${s.key}-${o.row.id}`} />))}
        </ul>
      )}
    </div>
  );
}

// The palette's body: recent activity (filtered while typing), then live results as each source answers.
export default function SmartSearchPanel({ q, session, res, options, activeKey, setActiveKey, onPick }) {
  const index = new Map(options.map((o, i) => [o.key, i]));
  const nav = (o) => ({ i: index.get(o.key), active: o.key === activeKey, onPick: () => onPick(o), onHover: () => setActiveKey(o.key) });
  const recentOpts = options.filter((o) => o.kind === 'recent');
  const searching = q.length >= session.minChars;
  const groups = res.q === q ? res.groups : {};
  const pending = session.sources.filter((s) => !groups[s.key] || groups[s.key].status === 'loading');
  const shown = session.sources.filter((s) => groups[s.key] && (groups[s.key].items?.length || ['slow', 'error'].includes(groups[s.key].status)));
  return (
    <>
      {session.recents === null && <Note testid="topbar-search-recent-loading">Loading recent activity…</Note>}
      {recentOpts.length > 0 && <RecentSection opts={recentOpts} nav={nav} q={q} onClear={session.clear} />}
      {!q && session.recents?.length === 0 && <Note testid="topbar-search-recent-empty">Records you open show up here, newest first.</Note>}
      {q && !searching && <Note testid="topbar-search-hint">Keep typing to search everything you can see.</Note>}
      {searching && shown.map((s) => (
        <ResultGroup key={s.key} s={s} g={groups[s.key]} q={q} nav={nav} opts={options.filter((o) => o.kind !== 'recent' && o.type === s.key)} />
      ))}
      {searching && pending.length > 0 && <Note testid="topbar-search-loading">Searching {pending.map((s) => s.noun).join(', ')}…</Note>}
      {searching && !pending.length && !shown.length && !recentOpts.length && (
        <Note testid="topbar-search-empty">Nothing you can see matches ‘{q}’.</Note>
      )}
    </>
  );
}
